import { client } from '../session';
import type { EnvironmentKind } from '../api';
import type { MetricSeries, MetricsRange } from './pods';

// ---------- dashboard status (`GET /status`, docs/contracts.md) ----------

export type EnvStatus = 'ok' | 'warn' | 'down' | 'idle' | 'error';
export type DbStatus = 'ok' | 'warn' | 'down' | 'stopped' | 'unknown';

export interface EnvironmentStatus {
  id: string;
  name: string;
  customer: string;
  kind: EnvironmentKind;
  discovered: boolean;
  clusterId: string | null;
  clusterName: string | null;
  namespaces: string[];
  status: EnvStatus;
  error: string | null;
  pods: {
    total: number;
    running: number;
    notReady: number;
    pending: number;
    failing: number;
    completed: number;
  };
  restarts: number;
  deployments: { total: number; available: number; degraded: number; down: number; paused: number };
  problems: {
    namespace: string;
    name: string;
    status: string;
    restarts: number;
    deployment: string | null;
  }[];
}

export interface ClusterStatus {
  id: string | null;
  name: string;
  projectId: string;
  location: string;
  state: string;
  version: string | null;
  nodes: number | null;
  registered: boolean;
  enabled: boolean;
  error: string | null;
}

export interface DatabaseStatus {
  key: string;
  name: string;
  kind: 'cloudsql' | 'alloydb' | 'registered';
  projectId: string | null;
  location: string | null;
  role: 'primary' | 'replica' | 'read-pool';
  engine: string;
  version: string;
  state: string;
  status: DbStatus;
  tier: string | null;
  privateIp: string | null;
  connections: number | null;
  maxConnections: number | null;
  /** 0–1. */
  cpu: number | null;
  serverId: string | null;
  serverName: string | null;
  environmentIds: string[];
  resourceName: string | null;
  error: string | null;
}

export interface JobSummary {
  queued: number;
  running: number;
  succeeded: number;
  failed: number;
  cancelled: number;
  recentFailures: {
    id: number;
    kind: string;
    serverId: string;
    database: string;
    error: string | null;
    createdAt: string;
  }[];
}

/** Sections are null when the caller lacks the read scope (pods:read → environments/clusters, db:read → databases/jobs). */
export interface HubStatus {
  generatedAt: string;
  environments: EnvironmentStatus[] | null;
  clusters: ClusterStatus[] | null;
  databases: DatabaseStatus[] | null;
  jobs: JobSummary | null;
  discovery: { projects: number; fetchedAt: string; errors: number } | null;
}

/** CPU (fraction 0–1), memory (bytes) and connections of one database instance over time, from Cloud Monitoring. */
export interface DbMetrics {
  range: MetricsRange;
  stepSeconds: number;
  from: string;
  to: string;
  kind: 'cloudsql' | 'alloydb';
  cpu: MetricSeries;
  /** Cloud SQL: used memory excluding cache, `limit` = the quota. */
  memoryUsed: MetricSeries | null;
  /** AlloyDB: the least available memory of its nodes (it reports no usage). */
  memoryAvailable: MetricSeries | null;
  connections: MetricSeries;
}

export const statusApi = {
  get: (signal?: AbortSignal) => client.get<HubStatus>('/status', { signal }),
  dbMetrics: (instance: string, range: MetricsRange) =>
    client.get<DbMetrics>(`/databases/metrics?${new URLSearchParams({ instance, range })}`),
};
